import { MAX_PHONE_NUMBER_LENGTH, MIN_PHONE_NUMBER_LENGTH } from '~/constants';

export type DeliveryId = 'DBI' | 'EXH' | 'EXB' | 'XXX';
export type StandardDeliveryId = 'DBI' | 'EXH';
export type ExpressDeliveryId = 'EXB' | 'XXX';
export type OrderType = 'express' | 'standard';

export type DeliveryObject = {
  name: string;
  id: DeliveryId;
  orderType: 'express' | 'standard';
  requiresStore?: boolean;
  requiresZipCode?: boolean;
  availableBetweenHours?: {
    start: number;
    end: number;
  };
  description?: string | { from: number; to: number; description: string }[];
  nextDayCutoffTime?: number;
  useContextZipCode?: boolean;
  useDestination?: boolean;
};

export type DeliveryField = {
  component: 'CInputGroup' | 'CTextareaGroup';
  props: {
    dataTest: string;
    name: string;
    placeholder: string;
    type: string;
    maxlength: number;
    minlength?: number;
    label?: string;
    counter?: boolean;
    required: boolean;
    prefill?: string;
    helperText?: string;
    disableOnPreFill?: boolean;
    errorMessages?: {
      [key: string]: string;
    };
  };
  errorMessages?: {
    [key: string]: string;
  };
  callback?: string;
};

const DELIVERY_ID = 'DBI';
const GET_IN_STORE_ID = 'EXH';
const STORE_EXPRESS = 'EXB';
const DELIVERY_EXPRESS = 'XXX';
const LATE_DELIVERY_EXPRESS = '24-12';

const ORDER_TYPES: { [key: string]: OrderType } = { EXPRESS: 'express', STANDARD: 'standard' };

const SORTED_DELIVERY_IDS = [DELIVERY_ID, GET_IN_STORE_ID, DELIVERY_EXPRESS, STORE_EXPRESS] as const;
const STANDARD_DELIVERIES = [DELIVERY_ID, GET_IN_STORE_ID] as const;
const EXPRESS_DELIVERIES = [DELIVERY_EXPRESS, STORE_EXPRESS] as const;
const STORE_DELIVERIES = [GET_IN_STORE_ID, STORE_EXPRESS] as const;

const DELIVERY_ID_OBJECT: DeliveryObject = {
  name: 'Utkörning',
  id: DELIVERY_ID,
  orderType: ORDER_TYPES.STANDARD,
};
const GET_IN_STORE_ID_OBJECT: DeliveryObject = {
  name: 'Leverans till butik',
  id: GET_IN_STORE_ID,
  orderType: ORDER_TYPES.STANDARD,
  requiresStore: true,
};
const DELIVERY_EXPRESS_OBJECT: DeliveryObject = {
  name: 'Expressutkörning',
  id: DELIVERY_EXPRESS,
  orderType: ORDER_TYPES.EXPRESS,
  requiresZipCode: true,
};

const LATE_DELIVERY_EXPRESS_OBJECT: DeliveryObject = {
  name: '24-12',
  //  The ERP interprets this as delivery express, hence the DELIVERY_EXPRESS id is used
  id: DELIVERY_EXPRESS,
  orderType: ORDER_TYPES.EXPRESS,
  requiresZipCode: true,
};

const STORE_EXPRESS_OBJECT: DeliveryObject = {
  name: 'Butiksexpress',
  id: STORE_EXPRESS,
  orderType: ORDER_TYPES.EXPRESS,
  requiresStore: true,
};

const REQUIRED_ACTIONS = {
  updateQuantity: 'update_quantity',
  activateDelivery: 'activate_delivery',
  addOrderInformation: 'missing_order_information',
  acceptConfirmation: 'not_confirmed',
};
const COUNTRY_CODES = { SE: 'SE', AX: 'AX' };

const ZIP_CODES_AX = new Set([
  22920, 22270, 22830, 22220, 22930, 22710, 22340, 22410, 22411, 22130, 22240, 22150, 22950, 22520, 22820, 22730, 22840,
  22610, 22630, 22100, 22101, 22110, 22111, 22120, 22140, 22160, 22310, 22430, 22810, 22720, 22530, 22330, 22910, 22550,
  22940, 22320,
]);

const ERP_DELIVERY_METHOD_MAP = {
  [DELIVERY_EXPRESS]: 'BE',
  [LATE_DELIVERY_EXPRESS]: '24-12',
  [STORE_EXPRESS]: 'BEH',
  [GET_IN_STORE_ID]: 'LE',
  [DELIVERY_ID]: null,
};

const DELIVERY_FIELDS_CONFIG_DAHL: DeliveryField[] = [
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-order-number-input',
      name: 'customerOrderNumber',
      placeholder: 'Eget ordernummer',
      type: 'text',
      maxlength: 30,
      required: true,
    },
    callback: 'validateOrderNumber',
    errorMessages: {
      maxCharacters: 'Max antal tecken är 30',
      noInput: 'Du har inte angett eget ordernummer',
      callback: 'Felaktigt PO-nummerformat',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-reference-input',
      name: 'customerReference',
      placeholder: 'Din referens',
      type: 'text',
      maxlength: 30,
      required: false,
      prefill: 'prefilledReferenceName',
    },
    errorMessages: {
      maxCharacters: 'Max antal tecken är 30',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'goods-labeling-input',
      name: 'goodsLabeling',
      placeholder: 'Godsmärkning',
      type: 'text',
      maxlength: 30,
      required: false,
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'phone-input',
      name: 'phone',
      placeholder: 'Mobiltelefon (för kontakt vid leverans)',
      type: 'tel',
      minlength: MIN_PHONE_NUMBER_LENGTH,
      maxlength: MAX_PHONE_NUMBER_LENGTH,
      helperText: 'Exempel: 0701234567',
      required: true,
      prefill: 'prefilledPhoneNumber',
    },
    errorMessages: {
      noInput: 'validation.any.empty',
      maxCharacters: 'validation.string.max',
      minCharacters: 'validation.string.min',
    },
  },
];

const DELIVERY_FIELDS_CONFIG_KONR: DeliveryField[] = [
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-order-number-input',
      name: 'customerOrderNumber',
      placeholder: 'Ordermärkning',
      type: 'text',
      maxlength: 35,
      required: true,
    },
    errorMessages: {
      maxCharacters: 'Max antal tecken är 35',
      noInput: 'Du har inte angett ordermärkning',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-reference-input',
      name: 'customerReference',
      placeholder: 'Din referens',
      type: 'text',
      maxlength: 50,
      required: true,
      prefill: 'prefilledReferenceName',
    },
    errorMessages: {
      noInput: 'Du måste ange referens',
      maxCharacters: 'Max antal tecken är 50',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'goods-labeling-input',
      name: 'goodsLabeling',
      placeholder: 'Meddelande till transportör',
      type: 'text',
      maxlength: 100,
      required: false,
    },
    errorMessages: {
      maxCharacters: 'Max antal tecken är 100',
      noInput: '',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'phone-input',
      name: 'phone',
      placeholder: 'Mobiltelefon (för kontakt vid leverans)',
      type: 'tel',
      minlength: MIN_PHONE_NUMBER_LENGTH,
      maxlength: MAX_PHONE_NUMBER_LENGTH,
      helperText: 'Exempel: 0701234567',
      required: false,
      prefill: 'prefilledPhoneNumber',
    },
    errorMessages: {
      maxCharacters: 'validation.string.max',
      minCharacters: 'validation.string.min',
    },
  },
  {
    component: 'CTextareaGroup',
    props: {
      dataTest: 'customer-service-message-input',
      name: 'customerServiceMessage',
      placeholder: 'Meddelande till kundservice',
      label: 'Meddelande till kundservice',
      type: 'text',
      counter: true,
      maxlength: 80,
      required: false,
    },
    errorMessages: {
      maxCharacters: 'Max antal tecken är 80',
    },
  },
];

const DELIVERY_FIELDS_CONFIG_OPTI: DeliveryField[] = [
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-reference-input',
      name: 'customerReference',
      placeholder: 'Din referens',
      type: 'text',
      maxlength: 30,
      required: true,
      prefill: 'prefilledReferenceName',
    },
    errorMessages: {
      noInput: 'Du måste ange referens',
      maxCharacters: 'Max antal tecken är 30',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'phone-input',
      name: 'phone',
      placeholder: 'Mobiltelefon (för kontakt vid leverans)',
      type: 'tel',
      minlength: MIN_PHONE_NUMBER_LENGTH,
      maxlength: MAX_PHONE_NUMBER_LENGTH,
      helperText: 'Exempel: 0701234567',
      required: true,
      prefill: 'prefilledPhoneNumber',
    },
    errorMessages: {
      noInput: 'validation.any.empty',
      maxCharacters: 'validation.string.max',
      minCharacters: 'validation.string.min',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-order-number-input',
      name: 'customerOrderNumber',
      placeholder: 'Märke / Arbetsplats',
      type: 'text',
      maxlength: 30,
      required: false,
      prefill: 'prefilledProjectId',
      disableOnPreFill: true,
    },
    errorMessages: {
      maxCharacters: 'Max antal tecken är 30',
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'customer-service-message-input',
      name: 'customerServiceMessage',
      placeholder: 'Leveransanvisning',
      type: 'text',
      maxlength: 30,
      required: false,
      errorMessages: {
        maxCharacters: 'Max antal tecken är 30',
      },
    },
  },
  {
    component: 'CInputGroup',
    props: {
      dataTest: 'goods-labeling-input',
      name: 'goodsLabeling',
      placeholder: 'Godsmärkning',
      type: 'text',
      maxlength: 30,
      required: false,
      errorMessages: {
        maxCharacters: 'Max antal tecken är 30',
      },
    },
  },
];

export {
  DELIVERY_ID,
  GET_IN_STORE_ID,
  STORE_EXPRESS,
  DELIVERY_EXPRESS,
  ORDER_TYPES,
  SORTED_DELIVERY_IDS,
  STANDARD_DELIVERIES,
  EXPRESS_DELIVERIES,
  STORE_DELIVERIES,
  DELIVERY_ID_OBJECT,
  GET_IN_STORE_ID_OBJECT,
  DELIVERY_EXPRESS_OBJECT,
  STORE_EXPRESS_OBJECT,
  LATE_DELIVERY_EXPRESS_OBJECT,
  REQUIRED_ACTIONS,
  ERP_DELIVERY_METHOD_MAP,
  LATE_DELIVERY_EXPRESS,
  COUNTRY_CODES,
  ZIP_CODES_AX,
  DELIVERY_FIELDS_CONFIG_DAHL,
  DELIVERY_FIELDS_CONFIG_KONR,
  DELIVERY_FIELDS_CONFIG_OPTI,
};
